import { useState } from 'react';
import type { TopReferrer, ChannelData, CampaignData, FilterKey } from '@/types';

type SourceTab = 'channels' | 'sources' | 'campaigns';

interface SourcesProps {
  referrers: TopReferrer[];
  channels: ChannelData[];
  campaigns: CampaignData[];
  isLoading?: boolean;
  onFilter?: (key: FilterKey, value: string) => void;
  onShowMore?: (tab: SourceTab) => void;
  hasMore?: boolean;
}

export function Sources({ referrers, channels, campaigns, isLoading, onFilter, onShowMore, hasMore }: SourcesProps) {
  const [activeTab, setActiveTab] = useState<SourceTab>('channels');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="animate-spin h-6 w-6 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div>
      {/* Tab switcher */}
      <div className="flex border-b border-gray-200 mb-3">
        {(['channels', 'sources', 'campaigns'] as SourceTab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'channels' ? 'Channels' : tab === 'sources' ? 'Sources' : 'Campaigns'}
          </button>
        ))}
      </div>

      {activeTab === 'channels' && <ChannelsView channels={channels} onFilter={onFilter} />}
      {activeTab === 'sources' && <SourcesView referrers={referrers} onFilter={onFilter} />}
      {activeTab === 'campaigns' && <CampaignsView campaigns={campaigns} onFilter={onFilter} />}

      {hasMore && (
        <button
          onClick={() => onShowMore?.(activeTab)}
          className="w-full mt-3 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
        >
          Show more
        </button>
      )}
    </div>
  );
}

function ChannelsView({ channels, onFilter }: { channels: ChannelData[]; onFilter?: (key: FilterKey, value: string) => void }) {
  if (channels.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No channel data available</div>;
  }

  const total = channels.reduce((sum, c) => sum + c.count, 0);
  const maxCount = Math.max(...channels.map((c) => c.count));

  return (
    <div className="space-y-2">
      {channels.map((channel) => {
        const percentage = total > 0 ? (channel.count / total) * 100 : 0;
        const barWidth = maxCount > 0 ? (channel.count / maxCount) * 100 : 0;

        return (
          <div
            key={channel.name}
            className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 rounded transition-colors"
            style={{ minHeight: '32px' }}
            onClick={() => onFilter?.('channel', channel.name)}
          >
            <div className="relative flex-1 mr-4 rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{ width: `${barWidth}%`, backgroundColor: 'rgba(249, 115, 22, 0.15)' }}
              />
              <span className="relative z-10 text-gray-700 truncate block px-2 py-1">{channel.name}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
              <span className="font-medium w-12 text-right">{channel.count.toLocaleString()}</span>
              <span className="w-14 text-right text-gray-500">{percentage.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SourcesView({ referrers, onFilter }: { referrers: TopReferrer[]; onFilter?: (key: FilterKey, value: string) => void }) {
  if (referrers.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No source data available</div>;
  }

  const total = referrers.reduce((sum, r) => sum + r.count, 0);
  const maxCount = Math.max(...referrers.map((r) => r.count));

  return (
    <div className="space-y-2">
      {referrers.map((referrer) => {
        const percentage = total > 0 ? (referrer.count / total) * 100 : 0;
        const barWidth = maxCount > 0 ? (referrer.count / maxCount) * 100 : 0;

        return (
          <div
            key={referrer.host || 'direct'}
            className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 rounded transition-colors"
            style={{ minHeight: '32px' }}
            onClick={() => onFilter?.('referrer_host', referrer.host)}
          >
            <div className="relative flex-1 mr-4 rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{ width: `${barWidth}%`, backgroundColor: 'rgba(249, 115, 22, 0.15)' }}
              />
              <div className="relative z-10 flex items-center gap-2 truncate px-2 py-1">
                {referrer.host && !referrer.host.includes('Direct') && (
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${referrer.host}&sz=32`}
                    alt=""
                    className="w-4 h-4 flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <span className="text-gray-700 truncate">{referrer.host || 'Direct / No Referrer'}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
              <span className="font-medium w-12 text-right">{referrer.count.toLocaleString()}</span>
              <span className="w-14 text-right text-gray-500">{percentage.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CampaignsView({ campaigns, onFilter }: { campaigns: CampaignData[]; onFilter?: (key: FilterKey, value: string) => void }) {
  if (campaigns.length === 0) {
    return <div className="text-center py-8 text-gray-500 text-sm">No campaign data available</div>;
  }

  const total = campaigns.reduce((sum, c) => sum + c.count, 0);
  const maxCount = Math.max(...campaigns.map((c) => c.count));

  return (
    <div className="space-y-2">
      {campaigns.map((campaign) => {
        const percentage = total > 0 ? (campaign.count / total) * 100 : 0;
        const barWidth = maxCount > 0 ? (campaign.count / maxCount) * 100 : 0;

        return (
          <div
            key={campaign.name}
            className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 rounded transition-colors"
            style={{ minHeight: '32px' }}
            onClick={() => onFilter?.('utm_campaign', campaign.name)}
          >
            <div className="relative flex-1 mr-4 rounded overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-300"
                style={{ width: `${barWidth}%`, backgroundColor: 'rgba(249, 115, 22, 0.15)' }}
              />
              <span className="relative z-10 text-gray-700 truncate block px-2 py-1">{campaign.name}</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600 flex-shrink-0">
              <span className="font-medium w-12 text-right">{campaign.count.toLocaleString()}</span>
              <span className="w-14 text-right text-gray-500">{percentage.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Sources;
